import type { AppearanceDefinition, AppearanceLayout, AppearanceStyle } from "./types";

export const DEFAULT_APPEARANCE: AppearanceStyle = "classic";

/**
 * Set on each appearance's root element (and on anything portalled outside it)
 * so appearance stylesheets can scope every rule and never reach classic.
 */
export const APPEARANCE_ATTRIBUTE = "data-redacto-appearance";

/**
 * Marks the notice's own roots. The notice and the Privacy Center share the
 * appearance attribute, so each one's glass stylesheet uses this to keep its
 * rules off the other when both render on one page.
 */
export const NOTICE_ROOT_ATTRIBUTE = "data-redacto-notice";

const CLASSIC_LAYOUT: AppearanceLayout = {
  sheet: false,
  collapsibleSections: false,
  switchControl: false,
  pairedFooter: false,
  loaderPill: false,
  sheetOverlays: false,
};

export const APPEARANCES: Readonly<Record<AppearanceStyle, AppearanceDefinition>> = {
  classic: { layout: CLASSIC_LAYOUT },
  // Minimal and soft restyle classic's surfaces and keep its layout.
  minimal: { layout: CLASSIC_LAYOUT },
  soft: { layout: CLASSIC_LAYOUT },
  glass: {
    layout: {
      sheet: true,
      collapsibleSections: true,
      // Checkboxes until the admin (or the host) picks switches, in every style.
      switchControl: false,
      pairedFooter: true,
      loaderPill: true,
      sheetOverlays: true,
    },
  },
};

export const isAppearance = (value: unknown): value is AppearanceStyle =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(APPEARANCES, value);

/**
 * Falls back to classic for anything unrecognised, so a typo or a value from a
 * newer SDK never breaks the host page.
 */
export const resolveAppearance = (value: unknown): AppearanceStyle =>
  isAppearance(value) ? value : DEFAULT_APPEARANCE;

export const getAppearanceLayout = (appearance: AppearanceStyle): AppearanceLayout =>
  APPEARANCES[appearance].layout;
